/** Garde-fous caméra : un getUserMedia « OK » ne garantit pas une image exploitable. */

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const CAMERA_GUARD_MESSAGES = {
  NO_STREAM: "Caméra non initialisée.",
  NO_VIDEO: "Aucune piste vidéo active. Autorisez la caméra et réessayez.",
  NO_AUDIO: "Aucune piste micro active. Autorisez le micro et réessayez.",
  NO_FRAMES: "La caméra ne diffuse pas d'image. Vérifiez qu'elle n'est pas utilisée ailleurs.",
  BLACK_VIDEO: "L'image caméra est noire. Vérifiez l'objectif et les permissions.",
  STATIC_VIDEO: "L'image caméra est figée. Fermez les autres apps qui utilisent la caméra, puis réactivez-la.",
};

function trackIsLive(track) {
  return Boolean(track && track.readyState === "live" && track.enabled !== false);
}

export function getLiveAvTracks(stream) {
  if (!stream) return { videoTrack: null, audioTrack: null };
  const videoTrack = stream.getVideoTracks().find((t) => trackIsLive(t)) || null;
  const audioTrack = stream.getAudioTracks().find((t) => trackIsLive(t)) || null;
  return { videoTrack, audioTrack };
}

/** Contrôle rapide des pistes (sans échantillonnage d'image). */
export function assertLiveAvTracks(stream) {
  if (!stream) {
    const err = new Error(CAMERA_GUARD_MESSAGES.NO_STREAM);
    err.code = "NO_STREAM";
    throw err;
  }
  const { videoTrack, audioTrack } = getLiveAvTracks(stream);
  if (!videoTrack) {
    const err = new Error(CAMERA_GUARD_MESSAGES.NO_VIDEO);
    err.code = "NO_VIDEO";
    throw err;
  }
  if (!audioTrack) {
    const err = new Error(CAMERA_GUARD_MESSAGES.NO_AUDIO);
    err.code = "NO_AUDIO";
    throw err;
  }
  return { videoTrack, audioTrack };
}

function analyzeRgba(data) {
  let sum = 0;
  let sumSq = 0;
  const n = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += luma;
    sumSq += luma * luma;
  }
  const mean = sum / n;
  const variance = Math.max(0, sumSq / n - mean * mean);
  return { mean, variance, data: data.slice(0) };
}

function meanAbsDiff(a, b) {
  let sum = 0;
  const n = a.length / 4;
  for (let i = 0; i < a.length; i += 4) {
    const la = 0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2];
    const lb = 0.299 * b[i] + 0.587 * b[i + 1] + 0.114 * b[i + 2];
    sum += Math.abs(la - lb);
  }
  return sum / n;
}

async function waitForVideoReady(video, timeoutMs) {
  const start = performance.now();
  while (performance.now() - start < timeoutMs) {
    if (video.videoWidth > 16 && video.videoHeight > 16 && video.readyState >= 2) {
      return;
    }
    await sleep(50);
  }
  const err = new Error(CAMERA_GUARD_MESSAGES.NO_FRAMES);
  err.code = "NO_FRAMES";
  throw err;
}

/**
 * Vérifie qu'un <video> branché sur le stream produit une image réelle
 * (pas noire, pas un placeholder caméra figé).
 */
export async function ensureCameraImageUsable(videoEl, stream, options = {}) {
  const {
    timeoutMs = 4500,
    sampleCount = 6,
    sampleIntervalMs = 180,
    sampleWidth = 160,
    sampleHeight = 90,
  } = options;

  assertLiveAvTracks(stream);
  if (!videoEl) {
    const err = new Error(CAMERA_GUARD_MESSAGES.NO_FRAMES);
    err.code = "NO_FRAMES";
    throw err;
  }

  if (videoEl.srcObject !== stream) {
    videoEl.srcObject = stream;
  }
  try {
    await videoEl.play();
  } catch {
    /* autoplay muted doit passer ; on continue sur readyState */
  }

  await waitForVideoReady(videoEl, timeoutMs);

  const canvas = document.createElement("canvas");
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true, alpha: false });
  if (!ctx) {
    const err = new Error(CAMERA_GUARD_MESSAGES.NO_FRAMES);
    err.code = "NO_FRAMES";
    throw err;
  }

  const samples = [];
  for (let i = 0; i < sampleCount; i += 1) {
    assertLiveAvTracks(stream);
    ctx.drawImage(videoEl, 0, 0, sampleWidth, sampleHeight);
    samples.push(analyzeRgba(ctx.getImageData(0, 0, sampleWidth, sampleHeight).data));
    if (i < sampleCount - 1) await sleep(sampleIntervalMs);
  }

  const avgMean = samples.reduce((s, x) => s + x.mean, 0) / samples.length;
  const avgVar = samples.reduce((s, x) => s + x.variance, 0) / samples.length;
  // Image quasi noire / vide (cas fond noir sans webcam).
  if (avgMean < 10 && avgVar < 25) {
    const err = new Error(CAMERA_GUARD_MESSAGES.BLACK_VIDEO);
    err.code = "BLACK_VIDEO";
    throw err;
  }

  let maxDiff = 0;
  for (let i = 1; i < samples.length; i += 1) {
    maxDiff = Math.max(maxDiff, meanAbsDiff(samples[i].data, samples[i - 1].data));
  }
  // Placeholder « caméra barrée » / frame unique figée : aucune variation capteur.
  // Un vrai flux webcam a presque toujours un peu de bruit même immobile.
  if (maxDiff < 0.35) {
    const err = new Error(CAMERA_GUARD_MESSAGES.STATIC_VIDEO);
    err.code = "STATIC_VIDEO";
    throw err;
  }

  return true;
}

export function stopMediaStream(stream) {
  if (!stream) return;
  try {
    stream.getTracks().forEach((t) => t.stop());
  } catch {
    /* ignore */
  }
}
